<!-- ══════════════════════════════════════════════════════════════════════════
     ORGANIZACION — la ficha de CrediTotal.

     🔴 REDISEÑO 2026-09-20, aprobado por Juan: *«demasiada caja, demasiado
     contenido, la vista se pierde»*, *«no quiero esas cajas grandes en el nuevo
     diseño»*.

     Lo que decide quien abre esta pantalla: SI CARGA LOS TRES DATOS FISCALES
     QUE FALTAN — y para decidirlo necesita saber qué queda trabado sin ellos.
     Todo lo que no sirve a esa decisión se fue.

     Lo que salió, y por qué (medido antes de tocar nada):
       · la barra «Ficha completa 40%» — el denominador metía `code` (bloqueado)
         y `name` (nunca vacío), así que NO PODIA BAJAR DE 40% aunque no hubiera
         ningún dato fiscal. Un número que no puede dar mal no informa nada.
       · las tres tarjetas de métrica — «Comercios» y «Tiendas de la red» son
         las de la pantalla de Comercios, y «Datos fiscales pendientes» era el
         mismo 3 que ya decían el aviso y la insignia: cuatro veces el mismo
         número en la misma pantalla.
       · la tarjeta «Dónde encaja» — repetía el renglón de la portada y volvía a
         pintar los dos conteos, tercera vez en la misma pantalla.
       · el aviso «El cliente es de la organización» — ya está escrito en
         Comercios y en la ficha del comercio. Queda un renglón al pie.
       · los subtítulos debajo de cada título de sección.

     Y la ficha dejó de pedir TODOS los comercios sin paginar para contar tres
     números que ya están en otra pantalla (ver el componente).

     Orden a propósito: DATOS FISCALES va ARRIBA de Identificación. Los tres
     campos que esta pantalla existe para llenar quedaban debajo del corte.
     ══════════════════════════════════════════════════════════════════════════ -->
<div class="kb-page kb-gateway">

  <!-- ── Encabezado ────────────────────────────────────────────────────────── -->
  <div class="kb-page-header">
    <div>
      <h1 class="kb-page-title flex items-center gap-2.5 !font-bold">
        <i class="fas fa-building shrink-0 text-[0.85em] text-muted-foreground" aria-hidden="true"></i>Organización
      </h1>
      <p class="kb-page-meta" *ngIf="!loading && !noExiste">
        {{ formData.name || 'Sin nombre' }}<span *ngIf="formData.code"> &middot; {{ formData.code }}</span>
      </p>
    </div>

    <div class="flex shrink-0 items-center gap-2" *ngIf="!loading && !noExiste">
      <ng-container *ngIf="!editando">
        <!-- 🔴 Se llama «Volver a consultar» y no «Actualizar»: actualizar se lee
             como MODIFICAR, y lo que hace es volver a pedirle la ficha al
             servidor. Es el mismo rótulo que usa Tipos y flujos — un verbo sin
             objeto en un botón es el defecto que este rediseño persigue. -->
        <!-- 2026-09-24: botón de icono, como el refrescar del Gateway. El rótulo
             sigue entero en `title` y `aria-label`: se ve al pasar el mouse y lo
             lee el lector de pantalla. -->
        <button type="button" class="kb-btn kb-btn-outline kb-btn-icon h-9 w-9"
                (click)="cargar()" [disabled]="loading"
                title="Volver a consultar la ficha" aria-label="Volver a consultar la ficha">
          <i class="fas fa-redo !text-foreground" aria-hidden="true"></i>
        </button>
        <button type="button" class="kb-btn kb-btn-outline"
                *ngIf="puedeEditar" (click)="entrarEnEdicion()">
          <i class="fas fa-pen !text-foreground" aria-hidden="true"></i>Editar datos
        </button>
        <span class="kb-badge kb-badge-contorno" *ngIf="!puedeEditar">
          <i class="fas fa-lock" aria-hidden="true"></i>Solo lectura
        </span>
      </ng-container>

      <ng-container *ngIf="editando">
        <button type="button" class="kb-btn kb-btn-ghost"
                [disabled]="guardando" (click)="cancelarEdicion()">Cancelar</button>
        <!-- 2026-09-24: el que guarda va en negro, como el botón principal del
             Gateway; el primario global de la consola todavía es cian. -->
        <button type="button" class="kb-btn kb-btn-primary !bg-foreground !text-background hover:!bg-foreground/90"
                [disabled]="guardando || !hayCambios" (click)="guardar()">
          <i class="fas fa-spinner fa-spin" *ngIf="guardando" aria-hidden="true"></i>
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </ng-container>
    </div>
  </div>

  <!-- ── Cargando ──────────────────────────────────────────────────────────── -->
  <!-- El esqueleto conserva la FORMA de lo que viene: dos bloques de pares (desde
       el 2026-09-24 ya no hay cinta arriba). Un esqueleto con otra silueta hace
       saltar la pantalla al cargar. -->
  <div *ngIf="loading" class="space-y-8" aria-live="polite" aria-busy="true">
    <div class="space-y-4">
      <div class="kb-skeleton h-3 w-28"></div>
      <div class="kb-skeleton h-5 w-full max-w-lg" *ngFor="let s of [1,2,3]"></div>
    </div>
    <div class="space-y-4">
      <div class="kb-skeleton h-3 w-28"></div>
      <div class="kb-skeleton h-5 w-full max-w-lg" *ngFor="let s of [1,2]"></div>
    </div>
  </div>

  <!-- ── No existe ─────────────────────────────────────────────────────────── -->
  <div *ngIf="!loading && noExiste" class="kb-empty">
    <div class="kb-empty-icono"><i class="fas fa-building" aria-hidden="true"></i></div>
    <p class="kb-empty-titulo">Todavía no hay una organización cargada</p>
    <p class="kb-empty-texto">
      Es el nivel raíz de la red: de ella cuelgan los comercios y sus tiendas.
      Hasta que exista, no se puede emitir documentación a nombre de CrediTotal.
    </p>
  </div>

  <!-- ══════════════════════════════════════════════════════════════════════
       LA FICHA
       ══════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="!loading && !noExiste" class="space-y-8">

    <!-- ══════════════ DATOS FISCALES — primero, porque es lo que se decide ══
         🔴 2026-09-24, con la gramática del Integration Gateway: la tarjeta que
         pide atención LLEVA el color de su estado —borde e insignia ámbar— y el
         aviso suelto que iba arriba («la cinta») se fue: su frase es ahora la
         descripción de la tarjeta, así que no se perdió nada y el estado quedó
         pegado al dato. Completa, la insignia pasa a verde; ya no hay cinta
         azul (cero azul y cero cian, regla de la casa). -->
    <section class="kb-tarjeta"
             [ngClass]="{ 'border-amber-300 dark:border-amber-500/40': pendientes.length && !editando }">
      <div class="kb-tarjeta-cab">
        <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
          <i class="fas fa-file-invoice text-muted-foreground" aria-hidden="true"></i>Datos fiscales
        </h2>
        <p class="m-0 text-sm text-muted-foreground" *ngIf="pendientes.length">
          <!-- 🔴 2026-09-25: decía «hasta que se carguen no se emite el pagaré…», y el pagaré se emite
               igual: ningún documento del núcleo lee estos datos todavía (medido: sólo los leen
               controllers/organization.js y el alta de comercios). La frase dice lo que es cierto. -->
          El pagaré, la factura al comercio y el estado de cuenta del cliente tienen que salir con estos
          datos. Hoy esos documentos todavía no los toman de aquí.
        </p>
        <p class="m-0 text-sm text-muted-foreground" *ngIf="!pendientes.length">
          Son los datos con los que tienen que salir el pagaré, la factura al comercio y el estado de cuenta
          del cliente. Hoy esos documentos todavía no los toman de aquí.
        </p>
        <div class="kb-tarjeta-accion col-start-2 row-span-2 row-start-1 self-start">
          <span class="kb-badge kb-badge-alerta" *ngIf="pendientes.length" role="status">
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
            {{ pendientes.length === 1 ? 'Falta 1' : 'Faltan ' + pendientes.length }}
          </span>
          <span class="kb-badge kb-badge-exito" *ngIf="!pendientes.length" role="status">
            <i class="fas fa-check-circle" aria-hidden="true"></i>Completos
          </span>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">

      <!-- Lectura -->
      <!-- 🔴 `kb-campos` y no `kb-datos`: con la etiqueta a la izquierda, tres
           campos dejaban el 70 % de la tarjeta en blanco. En grilla entran los
           tres en un renglón y la tarjeta se lee de un golpe. -->
      <dl class="kb-campos" *ngIf="!editando">
        <div class="kb-campo" *ngFor="let campo of CAMPOS_FISCALES">
          <dt class="kb-campo-et">{{ etiquetas[campo] }}</dt>
          <dd class="kb-campo-val">
            <span *ngIf="valor(campo)">{{ valor(campo) }}</span>
            <span class="kb-dato-falta" *ngIf="!valor(campo)">
              <i class="fas fa-exclamation-circle mr-1.5" aria-hidden="true"></i>Falta
            </span>
          </dd>
        </div>
      </dl>

      <!-- Edición — 🔴 la MISMA grilla de tres que la lectura (2026-09-24): al
           tocar «Editar datos» cada campo se vuelve editable en su lugar, en vez
           de saltar a una columna angosta que dejaba media tarjeta en blanco. -->
      <div class="grid gap-5 sm:grid-cols-3" *ngIf="editando">
        <div>
          <label class="kb-label" for="org-legal">Razón social</label>
          <input id="org-legal" type="text" class="kb-input"
                 [(ngModel)]="formData.legal_name" name="legal_name"
                 placeholder="El nombre con el que figura en el RIF">
          <!-- Este renglón sobrevivió al recorte porque dice algo que la etiqueta
               no dice: de dónde se copia el dato. Los que se fueron explicaban
               de qué se trataba la sección. -->
          <p class="kb-hint">Como figura en el registro mercantil.</p>
        </div>
        <div>
          <label class="kb-label" for="org-rif">RIF</label>
          <input id="org-rif" type="text" class="kb-input"
                 [(ngModel)]="formData.tax_id" name="tax_id"
                 (blur)="normalizarRif()" placeholder="J-00000000-0">
          <p class="kb-hint">Se acomoda solo al salir del campo.</p>
        </div>
        <div>
          <label class="kb-label" for="org-dir">Domicilio fiscal</label>
          <textarea id="org-dir" rows="2" class="kb-textarea"
                    [(ngModel)]="formData.address" name="address"
                    placeholder="La dirección que va en los documentos"></textarea>
        </div>
      </div>
      </div>
    </section>

    <!-- ══════════════ IDENTIFICACIÓN ═══════════════════════════════════════ -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
          <i class="fas fa-id-card text-muted-foreground" aria-hidden="true"></i>Identificación
        </h2>
        <p class="m-0 text-sm text-muted-foreground">
          El nombre y el código con los que la ve todo el sistema.
        </p>
      </div>
      <div class="kb-tarjeta-cuerpo">

      <dl class="kb-campos" *ngIf="!editando">
        <div class="kb-campo">
          <dt class="kb-campo-et">Nombre</dt>
          <dd class="kb-campo-val">{{ formData.name || '—' }}</dd>
        </div>

        <div class="kb-campo">
          <dt class="kb-campo-et">Código</dt>
          <dd class="kb-campo-val">
            <span class="font-mono">{{ formData.code || '—' }}</span>
          <!-- 🔴 Sin el número escrito a mano. Decía «los 51 millones de
               registros ya cargados»: un número medido que envejece solo y
               sigue prometiendo con la autoridad de una medición. -->
            <span class="kb-dato-nota mt-0.5 block">
              <i class="fas fa-lock mr-1" aria-hidden="true"></i>bloqueado — lo referencian todos los registros ya cargados
            </span>
          </dd>
        </div>
      </dl>

      <div class="grid gap-5 sm:grid-cols-3" *ngIf="editando">
        <div>
          <label class="kb-label" for="org-nombre">Nombre</label>
          <input id="org-nombre" type="text" class="kb-input"
                 [(ngModel)]="formData.name" name="name">
          <p class="kb-hint">Con este nombre se la ve en toda la consola.</p>
        </div>
        <div>
          <label class="kb-label" for="org-code">Código</label>
          <input id="org-code" type="text" class="kb-input" [value]="formData.code" disabled>
          <p class="kb-hint">No se puede cambiar: lo referencian todos los registros ya cargados.</p>
        </div>
      </div>
      </div>
    </section>

    <!-- ══════════════ El pie: la red, en un renglón ════════════════════════
         Reemplaza a la tarjeta «Dónde encaja» y al aviso azul. Sin conteos: la
         ficha ya no pide el listado de comercios para pintarlos. -->
    <div class="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5"
         *ngIf="!editando">
      <p class="m-0 max-w-3xl text-sm text-muted-foreground">
        Todos los comercios de la red y sus tiendas cuelgan de esta organización. El cliente
        también: su cupo y su historia son de CrediTotal, no del comercio donde compró.
      </p>
      <a routerLink="/console/merchants"
         class="inline-flex min-h-[24px] shrink-0 items-center py-1 text-sm font-semibold text-foreground underline decoration-muted-foreground/50 underline-offset-4 hover:decoration-foreground">
        Ver los comercios <i class="fas fa-arrow-right ml-1 text-xs" aria-hidden="true"></i>
      </a>
    </div>

  </div>
</div>
